var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __asyncValues = (this && this.__asyncValues) || function (o) {
    if (!Symbol.asyncIterator) throw new TypeError("Symbol.asyncIterator is not defined.");
    var m = o[Symbol.asyncIterator], i;
    return m ? m.call(o) : (o = typeof __values === "function" ? __values(o) : o[Symbol.iterator](), i = {}, verb("next"), verb("throw"), verb("return"), i[Symbol.asyncIterator] = function () { return this; }, i);
    function verb(n) { i[n] = o[n] && function (v) { return new Promise(function (resolve, reject) { v = o[n](v), settle(resolve, reject, v.done, v.value); }); }; }
    function settle(resolve, reject, d, v) { Promise.resolve(v).then(function(v) { resolve({ value: v, done: d }); }, reject); }
};
var __await = (this && this.__await) || function (v) { return this instanceof __await ? (this.v = v, this) : new __await(v); }
var __asyncGenerator = (this && this.__asyncGenerator) || function (thisArg, _arguments, generator) {
    if (!Symbol.asyncIterator) throw new TypeError("Symbol.asyncIterator is not defined.");
    var g = generator.apply(thisArg, _arguments || []), i, q = [];
    return i = Object.create((typeof AsyncIterator === "function" ? AsyncIterator : Object).prototype), verb("next"), verb("throw"), verb("return", awaitReturn), i[Symbol.asyncIterator] = function () { return this; }, i;
    function awaitReturn(f) { return function (v) { return Promise.resolve(v).then(f, reject); }; }
    function verb(n, f) { if (g[n]) { i[n] = function (v) { return new Promise(function (a, b) { q.push([n, v, a, b]) > 1 || resume(n, v); }); }; if (f) i[n] = f(i[n]); } }
    function resume(n, v) { try { step(g[n](v)); } catch (e) { settle(q[0][3], e); } }
    function step(r) { r.value instanceof __await ? Promise.resolve(r.value.v).then(fulfill, reject) : settle(q[0][2], r); }
    function fulfill(value) { resume("next", value); }
    function reject(value) { resume("throw", value); }
    function settle(f, v) { if (f(v), q.shift(), q.length) resume(q[0][0], q[0][1]); }
};
import { checkFilter, joinFilters, position } from "@hyper-hyper-space/hhs3_dag";
import { json } from "@hyper-hyper-space/hhs3_json";
import { formatValidationFailure, ValidationRejectedError } from "../validation.js";
// RootScopedDag: wraps a full Dag exposing only ScopedDag methods at runtime,
// ensuring root-level objects have the same method surface as nested objects.
export class RootScopedDag {
    constructor(dag) {
        this.dag = dag;
    }
    append(payload, meta, after) {
        return this.dag.append(payload, meta, after);
    }
    computeEntryHash(payload, after) {
        return this.dag.computeEntryHash(payload, after);
    }
    loadEntry(h) {
        return this.dag.loadEntry(h);
    }
    getFrontier() {
        return this.dag.getFrontier();
    }
    findCoverWithFilter(from, meta, predicate) {
        return this.dag.findCoverWithFilter(from, meta, predicate);
    }
    findConcurrentCoverWithFilter(from, concurrentTo, meta, predicate) {
        return this.dag.findConcurrentCoverWithFilter(from, concurrentTo, meta, predicate);
    }
    findMinimalCover(p) {
        return this.dag.findMinimalCover(p);
    }
    loadAllEntries() {
        return this.dag.loadAllEntries();
    }
}
export class NestedScopedDag {
    constructor(dag, scope) {
        this.dag = dag;
        this.scope = scope;
        this.empty = scope.startEmpty();
    }
    append(payload, meta, after) {
        return __awaiter(this, void 0, void 0, function* () {
            if (after === undefined) {
                if (this.empty) {
                    after = this.scope.startAt();
                }
                else {
                    after = yield this.dag.getFrontier();
                }
            }
            else {
                if (after.size === 0) {
                    after = this.scope.startAt();
                }
            }
            const wrappedPayload = this.scope.wrapPayload(payload, after);
            const wrappedMeta = this.scope.wrapMeta(meta, wrappedPayload, after);
            if (this.scope.validateWrappedPayload !== undefined) {
                const result = yield this.scope.validateWrappedPayload(wrappedPayload, wrappedMeta, after);
                if (!result.valid) {
                    throw new ValidationRejectedError(formatValidationFailure(result.why), result.why);
                }
            }
            const hash = yield this.dag.append(wrappedPayload, wrappedMeta, after);
            this.empty = false;
            return hash;
        });
    }
    computeEntryHash(payload, after) {
        if (after === undefined || after.size === 0) {
            after = this.scope.startAt();
        }
        return this.dag.computeEntryHash(this.scope.wrapPayload(payload, after), after);
    }
    loadEntry(h) {
        return __awaiter(this, void 0, void 0, function* () {
            let entry = yield this.dag.loadEntry(h);
            if (entry === undefined) {
                return undefined;
            }
            const at = position(...json.fromSet(entry.header.prevEntryHashes));
            const unwrappedPayload = this.scope.unwrapPayload(entry.payload, at);
            const unwrappedMeta = this.scope.unwrapMeta(entry.meta, unwrappedPayload, at);
            return Object.assign(Object.assign({}, entry), { payload: unwrappedPayload, meta: unwrappedMeta });
        });
    }
    getFrontier() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.dag.findCoverWithFilter(yield this.dag.getFrontier(), this.scope.baseFilter());
        });
    }
    findMinimalCover(p) {
        return this.dag.findCoverWithFilter(p, this.scope.baseFilter());
    }
    findCoverWithFilter(from, meta, predicate) {
        return this.dag.findCoverWithFilter(from, joinFilters(this.scope.baseFilter(), this.scope.wrapFilter(meta)), predicate);
    }
    findConcurrentCoverWithFilter(from, concurrentTo, meta, predicate) {
        return this.dag.findConcurrentCoverWithFilter(from, concurrentTo, joinFilters(this.scope.baseFilter(), this.scope.wrapFilter(meta)), predicate);
    }
    loadAllEntries() {
        const dag = this.dag;
        const scope = this.scope;
        return {
            [Symbol.asyncIterator]() {
                return __asyncGenerator(this, arguments, function* _a() {
                    var _b, e_1, _c, _d;
                    try {
                        for (var _e = true, _f = __asyncValues(dag.loadAllEntries()), _g; _g = yield __await(_f.next()), _b = _g.done, !_b; _e = true) {
                            _d = _g.value;
                            _e = false;
                            const entry = _d;
                            if (!checkFilter(entry.meta, scope.baseFilter())) {
                                continue;
                            }
                            const at = position(...json.fromSet(entry.header.prevEntryHashes));
                            const unwrappedPayload = scope.unwrapPayload(entry.payload, at);
                            const unwrappedMeta = scope.unwrapMeta(entry.meta, unwrappedPayload, at);
                            yield yield __await(Object.assign(Object.assign({}, entry), { payload: unwrappedPayload, meta: unwrappedMeta }));
                        }
                    }
                    catch (e_1_1) { e_1 = { error: e_1_1 }; }
                    finally {
                        try {
                            if (!_e && !_b && (_c = _f.return)) yield __await(_c.call(_f));
                        }
                        finally { if (e_1) throw e_1.error; }
                    }
                });
            },
        };
    }
}
